/*
  Bond, Oct 2026: accessibility fixes for keyboard and screen-reader users. Nothing here changes how the site
  looks or behaves for mouse and touch users. This file is ours, not Symmetry's. It is loaded right after main.js in layout/theme.liquid (both deferred),
  so Symmetry's components already exist when it runs. Check it still works after any Symmetry update.
*/
(() => {
  'use strict';

  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, iframe, [tabindex]:not([tabindex="-1"])';

  const isShown = (el) => !!el && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
  const focusablesIn = (root) => (root ? $$(FOCUSABLE, root).filter((el) => isShown(el) && !el.closest('[inert]')) : []);

  function srText(text) {
    const span = document.createElement('span');
    span.className = 'bond-vh';
    span.textContent = text;
    return span;
  }

  function debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  }

  const ICONS = {
    pause: '<svg aria-hidden="true" focusable="false" width="12" height="12" viewBox="0 0 12 12"><rect x="2" y="1" width="3" height="10" fill="currentColor"/><rect x="7" y="1" width="3" height="10" fill="currentColor"/></svg>',
    play: '<svg aria-hidden="true" focusable="false" width="12" height="12" viewBox="0 0 12 12"><path d="M3 1l8 5-8 5z" fill="currentColor"/></svg>'
  };

  // Keyboard mode: the focus ring in bond-custom.css only shows after the Tab key has been used, so mouse and touch
  // users never see it, even if they press an arrow key after a click. A real click or tap ends it.
  // Focus that came from a click or tap is remembered, so the arrow-key and focus-moving behaviour below stays
  // as it was for mouse and touch users.
  const root = document.documentElement;
  const usingKeyboard = () => root.classList.contains('bond-kbd');
  let lastPointerDown = 0;
  let focusFromPointer = false;
  const pointerJustUsed = () => Date.now() - lastPointerDown < 1000;
  document.addEventListener('keydown', (evt) => {
    if (evt.key === 'Tab') root.classList.add('bond-kbd');
  }, true);
  document.addEventListener('pointerdown', () => {
    root.classList.remove('bond-kbd');
    lastPointerDown = Date.now();
  }, { capture: true, passive: true });
  document.addEventListener('focusin', () => {
    focusFromPointer = pointerJustUsed();
  }, true);

  // Runs a setup function on matching elements now, and again whenever new elements appear
  // (app popups, the re-rendered cart drawer, lazy-loaded videos). Every setup function must be safe to repeat.
  const setups = [];
  function onEach(selector, setup) {
    $$(selector).forEach(setup);
    setups.push({ selector, setup });
  }
  let setupQueued = false;
  new MutationObserver((mutations) => {
    if (setupQueued || !mutations.some((m) => m.addedNodes.length)) return;
    setupQueued = true;
    requestAnimationFrame(() => {
      setupQueued = false;
      setups.forEach(({ selector, setup }) => $$(selector).forEach(setup));
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  /* 1. Product gallery (letter items 1 and 2).
     Only the image on screen can take focus or be read out. The thumbnail strip is a single Tab stop:
     the arrow keys move between images, as in the W3C "carousel with tabs for slide control" pattern.
     Symmetry's MediaGallery moves the "is-active" class on every change (arrows, swipe, thumbnail, colour choice),
     so that class drives everything here. */
  function setupGallery(gallery) {
    if (gallery.dataset.bondA11y) return;
    gallery.dataset.bondA11y = 'true';

    const slides = $$('.main-image .slider__item', gallery);
    const list = gallery.querySelector('.thumbnails .slider__grid');
    const items = list ? $$(':scope > .slider__item', list) : [];
    const tabs = items.map((item) => item.querySelector('.thumbnail')).filter(Boolean);

    if (list && tabs.length === items.length) {
      list.setAttribute('role', 'tablist');
      list.setAttribute('aria-label', 'Product images');
      items.forEach((item, i) => {
        item.setAttribute('role', 'presentation');
        const tab = tabs[i];
        tab.setAttribute('role', 'tab');
        const slide = slides.find((s) => s.dataset.mediaId === item.dataset.mediaId);
        if (slide) {
          if (!slide.id) slide.id = `bond-slide-${Math.random().toString(36).slice(2, 8)}-${slide.dataset.mediaId}`;
          slide.setAttribute('role', 'tabpanel');
          tab.setAttribute('aria-controls', slide.id);
        }
      });

      list.addEventListener('keydown', (evt) => {
        const current = evt.target.closest('[role="tab"]');
        if (!current || focusFromPointer) return;
        if (evt.key === ' ') {
          evt.preventDefault();
          current.click();
          return;
        }
        const visible = tabs.filter((tab) => !tab.parentElement.hidden);
        const index = visible.indexOf(current);
        let next = null;
        if (evt.key === 'ArrowRight') next = visible[(index + 1) % visible.length];
        else if (evt.key === 'ArrowLeft') next = visible[(index - 1 + visible.length) % visible.length];
        else if (evt.key === 'Home') next = visible[0];
        else if (evt.key === 'End') next = visible[visible.length - 1];
        if (!next) return;
        evt.preventDefault();
        next.focus();
        next.click();
      });
    }

    const sync = () => {
      const anyActive = slides.some((s) => s.classList.contains('is-active') && !s.hidden);
      slides.forEach((slide) => {
        slide.inert = anyActive && !slide.classList.contains('is-active');
      });
      if (!tabs.length) return;
      let selected = tabs.find((tab) => tab.parentElement.classList.contains('is-active'));
      if (!selected) selected = tabs.find((tab) => !tab.parentElement.hidden);
      tabs.forEach((tab) => {
        const on = tab === selected;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });
    };

    const observer = new MutationObserver(sync);
    slides.concat(items).forEach((el) => observer.observe(el, { attributes: true, attributeFilter: ['class', 'hidden'] }));
    sync();
  }
  onEach('media-gallery', setupGallery);

  // Zoom viewer: the enlarged image gets the same description as the image it shows.
  function setupGalleryViewer(modal) {
    if (modal.dataset.bondA11y) return;
    const zoom = modal.querySelector('.gallery-viewer__zoom-container');
    if (!zoom) return;
    modal.dataset.bondA11y = 'true';
    const base = (url) => (url || '').split('?')[0].replace(/^https?:/, '');
    const update = () => {
      zoom.querySelectorAll('img').forEach((img) => {
        const src = base(img.currentSrc || img.src);
        const thumb = $$('.gallery-viewer__thumb', modal).find((t) => base(t.dataset.zoomUrl) === src);
        const alt = thumb ? thumb.dataset.alt || '' : '';
        if (img.getAttribute('alt') !== alt) img.setAttribute('alt', alt);
      });
    };
    new MutationObserver(update).observe(zoom, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });
    update();
  }
  onEach('.gallery-viewer-modal', setupGalleryViewer);

  /* 2. Other carousels (cart drawer "You may like", product rows, logo rows).
     Slides that are completely off-screen can't take focus, but only while the carousel shows Previous/Next buttons,
     so every item stays reachable. Partly visible slides stay usable; with the keyboard they scroll fully into view. */
  function setupCarousel(carousel) {
    if (carousel.dataset.bondA11y || carousel.closest('media-gallery')) return;
    const slider = carousel.querySelector('.slider');
    const slides = $$('.slider__item', carousel);
    if (!slider || slides.length < 2) return;
    carousel.dataset.bondA11y = 'true';

    const hasNav = () => !carousel.hasAttribute('inactive') && $$('.slider-nav__btn, button[name="prev"], button[name="next"]', carousel).some(isShown);
    // The part of the row that can really be seen: the slider's box, narrowed by every parent that clips it.
    // The slider can be wider than its parent (the cart drawer's "You may like" row is), so a slide can touch
    // the slider's box and still be completely hidden.
    const visibleSpan = () => {
      let { left, right } = slider.getBoundingClientRect();
      for (let el = slider.parentElement; el && el !== document.body; el = el.parentElement) {
        if (getComputedStyle(el).overflowX !== 'visible') {
          const box = el.getBoundingClientRect();
          left = Math.max(left, box.left);
          right = Math.min(right, box.right);
        }
      }
      return { left, right };
    };
    const update = () => {
      const nav = hasNav();
      const span = visibleSpan();
      slides.forEach((slide) => {
        const box = slide.getBoundingClientRect();
        slide.inert = nav && !(box.right > span.left + 1 && box.left < span.right - 1);
      });
    };
    let queued = false;
    const queue = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        update();
      });
    };
    // Checked again when the row scrolls, when it comes on screen (for example when the cart drawer opens)
    // and when the window is resized.
    slider.addEventListener('scroll', queue, { passive: true });
    new IntersectionObserver(queue, { threshold: [0, 0.25, 0.5, 0.75, 1] }).observe(carousel);
    const drawer = carousel.closest('cart-drawer, .drawer');
    if (drawer) drawer.addEventListener('transitionend', queue);
    window.addEventListener('resize', debounce(queue, 250));
    update();
    // A partly visible item that gets keyboard focus scrolls fully into view, so focus is never off the edge.
    // Not for mouse clicks: the item would move under the pointer.
    carousel.addEventListener('focusin', (evt) => {
      const slide = evt.target.closest('.slider__item');
      if (slide && usingKeyboard()) slide.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  }
  onEach('carousel-slider', setupCarousel);

  /* 3. Desktop menu. Symmetry shows the top links in a copy of the menu (#proxy-nav), but the dropdowns live in the
     real menu further down the page, so Tab skipped them. Opening Shop or Learn by keyboard (Enter, Space or Down arrow)
     now moves focus into the dropdown. Esc closes it and returns to Shop/Learn, and tabbing past the last link
     continues with the next top-level link. Mouse behaviour is unchanged. */
  function setupDesktopNav() {
    const proxy = document.getElementById('proxy-nav');
    const mainNav = document.querySelector('main-navigation');
    const proxyList = proxy && proxy.querySelector('.navigation__tier-1');
    const realList = mainNav && mainNav.querySelector('.navigation__tier-1');
    if (!proxyList || !realList || proxy.dataset.bondA11y) return;
    proxy.dataset.bondA11y = 'true';

    const realItemFor = (proxyLink) => realList.children[Array.from(proxyList.children).indexOf(proxyLink.closest('.navigation__item'))];
    const proxyLinkFor = (realItem) => {
      const item = proxyList.children[Array.from(realList.children).indexOf(realItem)];
      return item && item.querySelector('.navigation__link');
    };
    const topItemOf = (el) => {
      let node = el;
      while (node && node.parentElement !== realList) node = node.parentElement;
      return node;
    };
    const panelOf = (realItem) => realItem && realItem.querySelector('.navigation__child-tier');
    const isOpen = (realItem) => realItem.classList.contains('navigation__item--show-children');
    // The Learn mega menu is rebuilt by our bt-13 menu script after Symmetry has attached its listeners,
    // so it is opened and closed by its class instead of Symmetry's hover events.
    const isCustom = (realItem) => realItem.classList.contains('bt-13-learn-mega-menu');
    const openedByKeyboard = new Set();
    const open = (proxyLink, realItem) => {
      if (isCustom(realItem)) {
        openedByKeyboard.add(realItem);
        realItem.classList.add('navigation__item--show-children');
      } else if (!isOpen(realItem)) {
        realItem.dispatchEvent(new Event('mouseenter'));
      }
      proxyLink.setAttribute('aria-expanded', 'true');
    };
    const close = (proxyLink, realItem) => {
      openedByKeyboard.delete(realItem);
      if (isCustom(realItem)) realItem.classList.remove('navigation__item--show-children');
      else realItem.dispatchEvent(new Event('mouseleave'));
      proxyLink.setAttribute('aria-expanded', 'false');
    };
    // The bt-13 script closes Learn on any mouseover outside it, and browsers fire one when the menu opens
    // under a resting pointer. While keyboard focus is in Learn (or on its link), keep it open.
    Array.from(realList.children).filter(isCustom).forEach((realItem) => {
      new MutationObserver(() => {
        if (!openedByKeyboard.has(realItem) || isOpen(realItem)) return;
        const active = document.activeElement;
        if (realItem.contains(active) || active === proxyLinkFor(realItem)) realItem.classList.add('navigation__item--show-children');
        else openedByKeyboard.delete(realItem);
      }).observe(realItem, { attributes: true, attributeFilter: ['class'] });
    });
    const focusFirst = (realItem, tries = 15) => {
      const first = focusablesIn(panelOf(realItem))[0];
      if (first && isOpen(realItem)) {
        first.focus();
      } else if (tries > 0) {
        setTimeout(() => focusFirst(realItem, tries - 1), 50);
      }
    };

    proxyList.addEventListener('keydown', (evt) => {
      const link = evt.target.closest('.navigation__item--with-children > .navigation__link');
      const realItem = link && realItemFor(link);
      if (!realItem || !panelOf(realItem)) return;
      if (evt.key === 'Enter' && isCustom(realItem)) {
        // Symmetry's handler can't open this menu and would reset aria-expanded, so it doesn't get the event.
        evt.preventDefault();
        evt.stopPropagation();
        if (isOpen(realItem)) {
          close(link, realItem);
        } else {
          open(link, realItem);
          focusFirst(realItem);
        }
      } else if (evt.key === 'Enter') {
        // Symmetry's own handler opens or closes the dropdown after this one runs; follow an opening with focus.
        if (!isOpen(realItem)) focusFirst(realItem);
      } else if (evt.key === ' ' || evt.key === 'ArrowDown') {
        evt.preventDefault();
        open(link, realItem);
        focusFirst(realItem);
      } else if (evt.key === 'Escape' && isOpen(realItem)) {
        close(link, realItem);
      }
    });

    realList.addEventListener('keydown', (evt) => {
      const realItem = topItemOf(evt.target);
      const proxyLink = realItem && proxyLinkFor(realItem);
      if (!proxyLink || !isOpen(realItem)) return;
      const links = focusablesIn(panelOf(realItem));
      if (evt.key === 'Escape') {
        evt.preventDefault();
        close(proxyLink, realItem);
        proxyLink.focus();
      } else if (evt.key === 'Tab' && !evt.shiftKey && evt.target === links[links.length - 1]) {
        evt.preventDefault();
        close(proxyLink, realItem);
        const all = focusablesIn(document.body);
        (all[all.indexOf(proxyLink) + 1] || proxyLink).focus();
      } else if (evt.key === 'Tab' && evt.shiftKey && evt.target === links[0]) {
        evt.preventDefault();
        proxyLink.focus();
      }
    });

    realList.addEventListener('focusout', (evt) => {
      const realItem = topItemOf(evt.target);
      const proxyLink = realItem && proxyLinkFor(realItem);
      if (!proxyLink) return;
      setTimeout(() => {
        const active = document.activeElement;
        if (realItem.contains(active) || active === proxyLink || mainNav.matches(':hover') || proxy.matches(':hover')) return;
        if (isOpen(realItem)) close(proxyLink, realItem);
      }, 0);
    });
  }
  setupDesktopNav();

  /* 4. Mobile menu. The menu is announced as a dialog, the Menu button says whether it is open, focus moves into
     the menu when it is opened by keyboard or a screen reader (not by a tap, so nothing changes on screen),
     and Esc closes it (Symmetry then returns focus to the Menu button). */
  function setupMobileNav(drawer) {
    if (drawer.dataset.bondA11y) return;
    drawer.dataset.bondA11y = 'true';
    if (!drawer.id) drawer.id = 'MobileNavigationDrawer';
    drawer.setAttribute('role', 'dialog');
    // Not aria-modal: iPhone VoiceOver would then hide the Close button, which sits outside the drawer.
    drawer.setAttribute('aria-label', 'Menu');

    const openers = () => $$('.mobile-nav-toggle').filter((btn) => !drawer.contains(btn) && !btn.classList.contains('header-shade'));
    const isOpen = () => document.body.classList.contains('reveal-mobile-nav');
    let wasOpen = null;
    const sync = () => {
      const open = isOpen();
      if (open === wasOpen) return;
      wasOpen = open;
      openers().forEach((btn) => {
        btn.setAttribute('aria-controls', drawer.id);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      if (open && !pointerJustUsed()) {
        setTimeout(() => {
          if (drawer.contains(document.activeElement)) return;
          const first = focusablesIn(drawer)[0];
          if (first) first.focus();
        }, 400);
      }
    };
    new MutationObserver(sync).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    sync();

    document.addEventListener('keydown', (evt) => {
      if (evt.key !== 'Escape' || !isOpen()) return;
      const closer = document.querySelector('.header-shade.mobile-nav-toggle') || drawer.querySelector('.mobile-nav-toggle');
      if (!closer) return;
      evt.preventDefault();
      closer.click();
    });
  }
  onEach('.mobile-navigation-drawer', setupMobileNav);

  // Symmetry's openMobileNav reads the timing of a menu link that our bt-13 menu replaces, so it threw a
  // "getComputedStyle" error every time the mobile menu opened and stopped before finishing. This is the same
  // routine (Symmetry 8.3.2) with a check for that link. Symmetry's own click handler calls theme.openMobileNav.
  function fixOpenMobileNav() {
    if (!window.theme || typeof window.theme.openMobileNav !== 'function' || window.theme.openMobileNav.bondFixed) return;
    const openMobileNav = () => {
      document.body.classList.add('enable-mobile-nav-transition');
      setTimeout(() => {
        document.body.classList.add('reveal-mobile-nav');
        const link = document.querySelector('.mobile-navigation-drawer .navigation__tier-1 > .navigation__item > .navigation__link');
        let delay = 0;
        if (link) {
          const cs = getComputedStyle(link);
          delay = (parseFloat(cs.transitionDelay.split(',')[0]) || 0) + (parseFloat(cs.transitionDuration.split(',')[0]) || 0);
        }
        setTimeout(() => document.body.classList.add('reveal-mobile-nav--revealed'), delay * 1000);
        if (typeof window.theme.manuallyLoadImages === 'function') window.theme.manuallyLoadImages(document.querySelector('.mobile-navigation-drawer'));
      }, 10);
    };
    openMobileNav.bondFixed = true;
    window.theme.openMobileNav = openMobileNav;
  }
  // Symmetry defines openMobileNav when the page has loaded, after this file runs, so ours goes in after it.
  document.addEventListener('DOMContentLoaded', fixOpenMobileNav);
  window.addEventListener('load', fixOpenMobileNav);

  // The Shop / Learn switch inside the mobile menu is announced as tabs, and the arrow keys move between them.
  function setupMenuTabs(nav) {
    if (nav.dataset.bondA11y) return;
    const scope = nav.closest('.mobile-navigation-drawer') || nav.parentElement;
    const buttons = $$('.bt-13-tab-button', nav);
    const panels = $$('.bt-13-tab-content', scope);
    if (!buttons.length || buttons.length !== panels.length) return;
    nav.dataset.bondA11y = 'true';
    nav.setAttribute('role', 'tablist');
    nav.setAttribute('aria-label', 'Menu sections');
    buttons.forEach((btn, i) => {
      btn.id = btn.id || `bond-menu-tab-${i}`;
      panels[i].id = panels[i].id || `bond-menu-panel-${i}`;
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-controls', panels[i].id);
      panels[i].setAttribute('role', 'tabpanel');
      panels[i].setAttribute('aria-labelledby', btn.id);
    });
    const sync = () => buttons.forEach((btn) => {
      const on = btn.classList.contains('bt-13-active');
      btn.setAttribute('aria-selected', on ? 'true' : 'false');
      btn.tabIndex = on ? 0 : -1;
    });
    new MutationObserver(sync).observe(nav, { attributes: true, subtree: true, attributeFilter: ['class'] });
    sync();
    nav.addEventListener('keydown', (evt) => {
      const index = buttons.indexOf(evt.target);
      if (index < 0 || focusFromPointer) return;
      let next = null;
      if (evt.key === 'ArrowRight') next = buttons[(index + 1) % buttons.length];
      else if (evt.key === 'ArrowLeft') next = buttons[(index - 1 + buttons.length) % buttons.length];
      if (!next) return;
      evt.preventDefault();
      next.focus();
      next.click();
    });
  }
  onEach('.bt-13-tab-navigation', setupMenuTabs);

  /* 5. Announcement bar. It changes message every 5 seconds, so it gets a pause button (WCAG 2.2.2). The button sits
     with Symmetry's own Previous/Next arrows, which only appear for keyboard and screen-reader users, so the bar
     looks and behaves exactly as before for everyone else. */
  function setupAnnouncementBar(bar) {
    if (bar.dataset.bondA11y || $$('.announcement', bar).length < 2 || typeof bar.pauseAnnouncements !== 'function') return;
    const controller = bar.querySelector('.announcement-bar__announcement-controller');
    const middle = bar.querySelector('.announcement-bar__middle');
    if (!controller || !middle) return;
    bar.dataset.bondA11y = 'true';

    // Symmetry restarts the rotation when the bar scrolls back into view; not if the visitor paused it.
    const proto = Object.getPrototypeOf(bar);
    if (!proto.bondPlayGuard) {
      const play = proto.playAnnouncements;
      proto.playAnnouncements = function playUnlessPaused(...args) {
        if (this.dataset.bondPaused === 'true') return undefined;
        return play.apply(this, args);
      };
      proto.bondPlayGuard = true;
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'announcement-button bond-announcement-pause';
    const render = () => {
      const paused = bar.dataset.bondPaused === 'true';
      button.setAttribute('aria-label', paused ? 'Play announcements' : 'Pause announcements');
      button.innerHTML = paused ? ICONS.play : ICONS.pause;
    };
    // While focus is in the bar Symmetry has already stopped the rotation; leaving it restarts unless paused.
    button.addEventListener('click', () => {
      const pause = bar.dataset.bondPaused !== 'true';
      bar.dataset.bondPaused = pause ? 'true' : 'false';
      if (pause) bar.pauseAnnouncements();
      render();
    });
    controller.appendChild(button);
    // Symmetry restarts the rotation when focus leaves the bar; keep it stopped if the visitor paused it.
    middle.addEventListener('focusout', () => {
      if (bar.dataset.bondPaused === 'true') bar.pauseAnnouncements();
    });
    render();
  }
  onEach('announcement-bar', setupAnnouncementBar);

  /* 6. Videos that play by themselves (muted and looping, without controls) get a pause button (WCAG 2.2.2).
     It is hidden until it gets keyboard focus (like a skip link), so the video looks the same for everyone else. */
  function setupAutoplayVideo(video) {
    if (video.dataset.bondA11y || video.controls || !video.hasAttribute('autoplay')) return;
    let host = video.closest('.video-section, .gallery__image, video-component') || video.parentElement;
    const link = host.closest('a');
    if (link) host = link.parentElement;
    if (!host || host.querySelector('.bond-video-toggle, button[aria-label*="ause" i]')) return;
    video.dataset.bondA11y = 'true';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'bond-video-toggle';
    const render = () => {
      const paused = video.dataset.bondUserPaused === 'true';
      button.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
      button.innerHTML = paused ? ICONS.play : ICONS.pause;
    };
    button.addEventListener('click', (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      const paused = video.dataset.bondUserPaused === 'true';
      video.dataset.bondUserPaused = paused ? 'false' : 'true';
      if (paused) {
        const attempt = video.play();
        if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
      } else {
        video.pause();
      }
      render();
    });
    // The theme restarts videos when they scroll into view; respect the visitor's choice.
    video.addEventListener('play', () => {
      if (video.dataset.bondUserPaused === 'true') video.pause();
    });
    host.appendChild(button);
    render();
  }
  onEach('video', setupAutoplayVideo);

  /* 7. Links that open a new tab say so before they are used (letter item 3): screen readers hear
     "(opens in a new tab)". Nothing changes on screen and the links behave as before. This covers theme links,
     links to other sites, page content and apps such as the BOGOS free-gift popup. */
  const MENTIONS_NEW_TAB = /new (tab|window)/i;
  function markNewTabLink(link) {
    if (link.dataset.bondNewTab) return;
    link.dataset.bondNewTab = 'true';
    const label = link.getAttribute('aria-label');
    if (MENTIONS_NEW_TAB.test(`${label || ''} ${link.textContent} ${link.title || ''}`)) return;
    if (label) link.setAttribute('aria-label', `${label} (opens in a new tab)`);
    else link.appendChild(srText(' (opens in a new tab)'));
  }
  // Theme setting "Open external links in new tab" is on: Symmetry adds target="_blank" to links to other sites
  // (for example help.bond-touch.com) only when they are clicked, so those links get the warning too.
  const opensNewTab = (link) => {
    if (link.target === '_blank') return true;
    if (!(window.theme && window.theme.settings && window.theme.settings.externalLinksNewTab)) return false;
    try {
      const url = new URL(link.href, window.location.href);
      return /^https?:$/.test(url.protocol) && url.hostname !== window.location.hostname;
    } catch (err) {
      return false;
    }
  };
  onEach('a[href]', (link) => {
    if (link.dataset.bondNewTab || link.dataset.bondTabChecked === link.href) return;
    link.dataset.bondTabChecked = link.href;
    if (opensNewTab(link)) markNewTabLink(link);
  });

  /* 8. Sideways-scrolling rows with nothing focusable inside can still be scrolled by keyboard (WCAG 2.1.1). */
  function checkScroller(el) {
    // Rows inside something closed (for example the cart drawer) are skipped; their items are counted when shown.
    if (!isShown(el)) return;
    const scrollable = el.scrollWidth > el.clientWidth + 1 && /(auto|scroll)/.test(getComputedStyle(el).overflowX);
    if (scrollable && !el.hasAttribute('tabindex') && focusablesIn(el).length === 0) {
      el.tabIndex = 0;
      el.dataset.bondScroller = 'true';
      if (!el.getAttribute('role')) el.setAttribute('role', 'region');
      if (!el.getAttribute('aria-label')) {
        const section = el.closest('.shopify-section');
        const heading = section && section.querySelector('h2, h3');
        el.setAttribute('aria-label', (heading && heading.textContent.trim()) || 'Scrollable content');
      }
    } else if (!scrollable && el.dataset.bondScroller) {
      el.removeAttribute('tabindex');
      el.removeAttribute('role');
      el.removeAttribute('aria-label');
      delete el.dataset.bondScroller;
    }
  }
  const checkScrollers = () => $$('.slider').forEach(checkScroller);
  window.addEventListener('load', checkScrollers);
  window.addEventListener('resize', debounce(checkScrollers, 300));

  /* 9. Sold-out options are greyed out on screen; screen readers now hear "unavailable" too. */
  function syncUnavailable(input) {
    const label = (input.id && document.querySelector(`label[for="${CSS.escape(input.id)}"]`)) || null;
    if (!label) return;
    const note = label.querySelector('.bond-unavailable');
    const unavailable = input.classList.contains('is-unavailable');
    if (unavailable && !note) {
      const span = srText(', unavailable');
      span.classList.add('bond-unavailable');
      label.appendChild(span);
    } else if (!unavailable && note) {
      note.remove();
    }
  }
  function setupVariantPicker(picker) {
    if (picker.dataset.bondA11y) return;
    picker.dataset.bondA11y = 'true';
    $$('input[type="radio"]', picker).forEach(syncUnavailable);
    new MutationObserver((mutations) => mutations.forEach((m) => {
      if (m.target.matches('input[type="radio"]')) syncUnavailable(m.target);
    })).observe(picker, { attributes: true, subtree: true, attributeFilter: ['class'] });
  }
  onEach('variant-picker', setupVariantPicker);

  /* 10. Menu links. A picture link that repeats the text link next to it (same address, no name of its own) is
     taken out of the Tab order and hidden from screen readers. "View Product" style links in the menu's side
     panel also say which product they open. */
  const nameOf = (link) => (link.getAttribute('aria-label') || link.textContent || $$('img[alt]', link).map((img) => img.alt).join(' ') || link.title || '').trim();
  function tidyMenuLink(link) {
    if (link.dataset.bondMenuLink) return;
    link.dataset.bondMenuLink = 'true';
    const href = link.getAttribute('href');
    if (!nameOf(link)) {
      const scope = link.closest('li, .navigation__item, .bt-13-side-item') || link.parentElement;
      const twin = $$('a[href]', scope).find((other) => other !== link && other.getAttribute('href') === href && nameOf(other));
      if (twin) {
        link.tabIndex = -1;
        link.setAttribute('aria-hidden', 'true');
      }
      return;
    }
    const card = link.closest('.bt-13-side-item');
    const title = card && card.querySelector('h3');
    const text = link.textContent.trim();
    if (title && text && !text.includes(title.textContent.trim()) && !link.getAttribute('aria-label')) {
      link.setAttribute('aria-label', `${text}: ${title.textContent.trim()}`);
    }
  }
  onEach('.mobile-navigation-drawer a[href], .navigation__child-tier a[href]', tidyMenuLink);

  // 11. Reviews widget (Opinew): its "sort by" dropdown has no name. A dropdown isn't named by the option it shows,
  // so it gets one even when it has text.
  onEach('[id^="opw-filter-"][id$="-trigger"]', (btn) => {
    if (!btn.getAttribute('aria-label') && !btn.getAttribute('aria-labelledby')) btn.setAttribute('aria-label', 'Sort reviews');
  });

  // 12. Close buttons that only show an icon (for example the × on the sale banner) are read as "Close".
  onEach('.prime-banner__close, button[class*="close"]:not([aria-labelledby])', (btn) => {
    if (!nameOf(btn)) btn.setAttribute('aria-label', 'Close');
  });

  // 13. Quantity boxes (cart drawer and cart page): VoiceOver read the number as a share of the allowed range
  // ("0.1%"). They now say the number itself, kept up to date as the quantity changes.
  const syncQuantity = (input) => {
    if (input.getAttribute('aria-valuetext') !== input.value) input.setAttribute('aria-valuetext', input.value);
  };
  onEach('input[type="number"]', (input) => {
    syncQuantity(input);
    if (input.dataset.bondQuantity) return;
    input.dataset.bondQuantity = 'true';
    ['input', 'change'].forEach((type) => input.addEventListener(type, () => syncQuantity(input)));
  });

  // 14. Phone menu position (not a letter item). The menu CSS pins the drawer 60px from the top, which is the
  // header's height. When the announcement bar shows above the header, the drawer covered the logo and the ×
  // button, so the drawer now starts just below the header. When the header is at the top, the CSS values stay.
  const phoneMenu = window.matchMedia('(max-width: 768px)');
  const placeMenu = () => {
    const drawer = document.querySelector('.bt-13-test .mobile-navigation-drawer');
    const header = document.querySelector('#pageheader');
    if (!drawer || !header) return;
    const top = phoneMenu.matches ? Math.round(header.getBoundingClientRect().bottom) - 1 : 60;
    if (top > 60) {
      drawer.style.top = `${top}px`;
      drawer.style.height = `calc(100% - ${top + 43}px)`;
    } else {
      drawer.style.removeProperty('top');
      drawer.style.removeProperty('height');
    }
  };
  new MutationObserver(placeMenu).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  window.addEventListener('scroll', () => { if (document.body.classList.contains('reveal-mobile-nav')) placeMenu(); }, { passive: true });
  window.addEventListener('resize', placeMenu);
  placeMenu();
})();
